import type { QuickQuestionRow } from './api';

/** One question asked more than once on one database: its runs, newest
 *  first. The latest run is the card; the rest are the series (80.3). */
export interface QuestionSeries {
  key: string;
  question: string;
  db_id: string;
  /** The newest run: the card. */
  latest: QuickQuestionRow;
  /** Every run, newest first, the latest included. */
  runs: QuickQuestionRow[];
}

const seriesKey = (row: QuickQuestionRow) =>
  `${row.db_id}\u0000${row.question_text.trim().replace(/\s+/g, ' ').toLowerCase()}`;

/** Group recent questions into series by their text and database, each
 *  series ordered newest first, the series themselves by their latest run. */
export function groupRuns(rows: QuickQuestionRow[]): QuestionSeries[] {
  const byKey = new Map<string, QuestionSeries>();
  for (const row of rows) {
    const key = seriesKey(row);
    const series = byKey.get(key);
    if (series) series.runs.push(row);
    else
      byKey.set(key, {
        key,
        question: row.question_text,
        db_id: row.db_id,
        latest: row,
        runs: [row],
      });
  }
  for (const s of byKey.values()) {
    s.runs.sort((a, b) => (a.asked_at < b.asked_at ? 1 : -1));
    s.latest = s.runs[0] ?? s.latest;
    s.question = s.latest.question_text;
  }
  return [...byKey.values()].sort((a, b) => (a.latest.asked_at < b.latest.asked_at ? 1 : -1));
}

export interface SiteChange {
  ts: string;
  action: string;
  target: string | null;
}

const CHANGE_WORDS: Record<string, string> = {
  'data.load': 'load',
  'data.snapshot.restore': 'restore',
  'data.source.delete': 'source removed',
  'data.graph.create': 'graph created',
  'data.graph.delete': 'graph removed',
  'data.graph.clear': 'graph cleared',
  'data.import.rdf.commit': 'import',
  'rules.create': 'rule added',
  'rules.update': 'rule changed',
  'rules.delete': 'rule removed',
  'llm.correction.create': 'correction added',
  'llm.correction.delete': 'correction removed',
};

/** A change in plain words: "load urn:tres:data", "rule changed CP-EAD". */
export function changeWords(c: SiteChange): string {
  const word =
    CHANGE_WORDS[c.action] ??
    (c.action.startsWith('schema.')
      ? 'ontology edit'
      : c.action.startsWith('vocab.')
        ? 'vocabulary edit'
        : c.action);
  return c.target ? `${word} ${c.target}` : word;
}

/** The audit log stamps UTC without a zone; read it as UTC. */
export function changeMoment(ts: string): number {
  const iso = ts.includes('T') ? ts : `${ts.replace(' ', 'T')}Z`;
  return Date.parse(iso);
}

/** The changes that fell between a run and the run before it, keyed by the
 *  later run's id, so a series can say what moved between two answers. */
export function changesBetweenRuns(
  runs: QuickQuestionRow[],
  changes: SiteChange[],
): Map<string, SiteChange[]> {
  const out = new Map<string, SiteChange[]>();
  runs.forEach((run, i) => {
    const before = runs[i + 1];
    if (!before) return;
    const later = changeMoment(run.asked_at);
    const earlier = changeMoment(before.asked_at);
    out.set(
      run.id,
      changes.filter((c) => {
        const at = changeMoment(c.ts);
        return at > earlier && at <= later;
      }),
    );
  });
  return out;
}

/** The first line of an answer, for the series list. */
export function firstLine(text: string): string {
  const line = text.split('\n').find((l) => l.trim() !== '') ?? '';
  return line.length > 160 ? `${line.slice(0, 157)}…` : line;
}
